import { useState } from 'react';

import { Sheet, SheetContent, SheetTitle } from '@/components/ui/sheet';
import { WorkspaceYearSync } from '@/features/program-year/components/WorkspaceYearSync';
import { useDisclosure } from '@/hooks/useDisclosure';
import { cn } from '@/lib/utils';
import { usePreferencesStore } from '@/stores/preferences';

import { WorkspaceBody } from './WorkspaceBody';
import { WorkspaceFooter } from './WorkspaceFooter';
import { WorkspaceSidebar } from './WorkspaceSidebar';
import { WorkspaceTopbar } from './WorkspaceTopbar';

/**
 * 워크스페이스 레이아웃 — Top / Left Nav / Content(Body) / Footer 4분할.
 * 라우트 전환 시 Body(Outlet)만 교체된다. lg(1024px) 미만에서는 Left Nav 가 시트(드로어)로 전환.
 * 데스크톱 Left Nav 는 접을 수 있다(아이콘 레일 72px) — 접힌 상태에서 마우스 오버·키보드 포커스가 들어오면
 * 본문을 밀지 않고 240px 로 잠시 펼쳐졌다가(플라이아웃) 벗어나면 다시 접힌다.
 */
export function WorkspaceLayout() {
  const menu = useDisclosure();
  const collapsed = usePreferencesStore((s) => s.sidebarCollapsed);
  const toggleSidebar = usePreferencesStore((s) => s.toggleSidebar);
  const [peeking, setPeeking] = useState(false);
  const expanded = !collapsed || peeking;

  return (
    <div className="min-h-screen bg-background">
      {/* 기준 연도 ↔ 주소·기억 동기화(specs/032) — 렌더 없음 */}
      <WorkspaceYearSync />
      {/* Left Nav — 데스크톱 고정. 접힘 + 오버/포커스 = 플라이아웃(본문 여백은 레일 폭 유지) */}
      <div
        className={cn(
          'fixed inset-y-0 left-0 z-30 hidden transition-[width,box-shadow] duration-200 lg:block',
          expanded ? 'w-sidebar' : 'w-sidebar-rail',
          collapsed && peeking && 'shadow-lg',
        )}
        onMouseEnter={() => collapsed && setPeeking(true)}
        onMouseLeave={() => setPeeking(false)}
        onFocus={() => collapsed && setPeeking(true)}
        onBlur={(e) => {
          if (!e.currentTarget.contains(e.relatedTarget)) setPeeking(false);
        }}
      >
        <WorkspaceSidebar rail={!expanded} collapsed={collapsed} onToggleCollapse={toggleSidebar} />
      </div>
      {/* Left Nav — 모바일 시트(항상 전체 폭) */}
      <Sheet open={menu.isOpen} onOpenChange={menu.setOpen}>
        <SheetContent side="left" className="w-sidebar border-0 bg-sidebar p-0">
          <SheetTitle className="sr-only">메뉴</SheetTitle>
          <WorkspaceSidebar onNavigate={menu.close} />
        </SheetContent>
      </Sheet>

      <div
        className={cn(
          'flex min-h-screen min-w-0 flex-col transition-[margin] duration-200',
          collapsed ? 'lg:ml-sidebar-rail' : 'lg:ml-sidebar',
        )}
      >
        <WorkspaceTopbar onOpenMenu={menu.open} />
        <WorkspaceBody />
        <WorkspaceFooter />
      </div>
    </div>
  );
}
